import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import DataObjectIcon from '@mui/icons-material/DataObject'
import SsidChartOutlinedIcon from '@mui/icons-material/SsidChartOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { useContextHook } from '../store/context'
import { Log } from '../types/log'
import { get, remove, setPageTitle } from '../utils/service'
import { LegacyDatapoint, Datapoint } from '../types/datapoint'
import { LogEvent } from '../types/logEvent'
import LogChart from '../components/charts/logChart'
import {
  convertToTempScale,
  datapointsToSeries,
  getChartMaxTemp,
  getExpectedProfileSeries,
  getLogEvents,
  getPlotSeriesFromLog,
  getRorInterval,
  getValueSuffix,
} from '../utils/graphUtils'
import Comments from '../components/comments/comments'
import { Sensorconfig } from '../types/sensorconfig'
import { getDefaultProfileData, getMainProfileParam, isBtBasedProfile } from '../utils/profileEditorUtils'
import { ProfileType } from '../types/enums/profileType'
import { Profile } from '../types/profile'
import LogChartTitle from '../components/logs/logChartTitle'
import { EventType, EventTypeString } from '../types/enums/eventType'
import {
  calculateProfilePoints,
  getLogName,
  loadLogProfile,
  getEventsFromLogOrDatapoints,
  legacyDatapointsToDatapoints,
} from '../utils/logUtils'
import LogInfoTable from '../components/logs/logInfoTable'
import ConfirmationDialog from '../components/dialog/confirmationDialog'
import Alert from '../components/alert/alert'
import InventorySelector from '../components/inventory/inventorySelector'
import BatchWeightDialog from '../components/logs/batchWeightDialog'
import CreateProfileFromLogModal from '../components/logs/createProfileFromLogModal'
import { BasicDataParam } from '../types/enums/graphDataParam'
import ViewStepButtons from '../components/fields/viewStepButtons'
import TooltipIconButton from '../components/fields/tooltipIconButton'
import { PagedResponse } from '../types/pagedResponse'
import { CuppingSession } from '../types/cuppingSession'
import { GraphDatapoint } from '../types/graphSeriesConfig'
import { SeriesType } from '../types/enums/seriesType'
import { getP2000MaxWeight, getL100MaxWeight } from '../utils/weightUtils'
import InfoElement from '../components/fields/infoElement'
import CuppingLinkButton from '../components/cupping/cuppingLinkButton'
import { ViewStep } from '../types/enums/viewStep'
import { LEFT_COLUMN_WIDTH, RIGHT_COLUMN_WIDTH } from '../utils/theme'
import EditEventsDialog from '../components/logs/editEventsDialog'
import BatchColorDialog from '../components/logs/batchColorDialog'
import CompareLogDialog from '../components/logs/compareLogDialog'

const LogView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { state } = useContextHook()

  const fullscreenRef = React.useRef<HTMLElement>(null)

  const [isLoadingLog, setIsLoadingLog] = React.useState<boolean>(false)
  const [hasLoadLogError, setHasLoadLogError] = React.useState<boolean>(false)

  const [currentStep, setCurrentStep] = React.useState<ViewStep>(ViewStep.Left)
  const [logData, setLogData] = React.useState<Log | null>(null)
  const [datapoints, setDatapoints] = React.useState<Array<Datapoint> | null>(null)
  const [events, setEvents] = React.useState<Array<LogEvent> | null>(null)
  const [isCreateProfileModalOpen, setIsCreateProfileModalOpen] = React.useState<boolean>(false)

  const [profileData, setProfileData] = React.useState<Profile | null>(null)
  const [isProfileDataAvailable, setIsProfileDataAvailable] = React.useState<boolean>(true)
  const [isLoadingProfileData, setIsLoadingProfileData] = React.useState<boolean>(false)

  const [isCompareLogModalOpen, setIsCompareLogModalOpen] = React.useState<boolean>(false)
  const [isDeleteLogModalOpen, setIsDeleteLogModalOpen] = React.useState<boolean>(false)
  const [isDeletingLog, setIsDeletingLog] = React.useState<boolean>(false)
  const [hasDeleteLogSuccess, setHasDeleteLogSuccess] = React.useState<boolean>(false)
  const [hasDeleteError, setHasDeleteError] = React.useState<boolean>(false)
  const [isInventoryDialogOpen, setIsInventoryDialogOpen] = React.useState<boolean>(false)
  const [isBatchWeightDialogOpen, setIsBatchWeightDialogOpen] = React.useState<boolean>(false)
  const [isBatchColorDialogOpen, setIsBatchColorDialogOpen] = React.useState<boolean>(false)
  const [isEventsDialogOpen, setIsEventsDialogOpen] = React.useState<boolean>(false)
  const [isNameDialogOpen, setIsNameDialogOpen] = React.useState<boolean>(false)
  const [logCuppingSessions, setLogCuppingSessions] = React.useState<Array<CuppingSession> | undefined>(undefined)
  const [hasLogCuppingSessionsError, setHasLogCuppingSessionsError] = React.useState<boolean>(false)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const getSensorconfig = (): Sensorconfig | null => {
    return logData?.sensor_config || null
  }

  const shouldShowStep = (step: ViewStep) => {
    return !isScreenBelowMdBreakpoint || (isScreenBelowMdBreakpoint && currentStep === step)
  }

  const createProfileFromLog = (profileType: ProfileType): void => {
    // remove extra points where previous and next value is the same y-axis value
    const calculateProfilePointsFromSeries = (profilePoints: Array<GraphDatapoint>): number[][] => {
      const interpolatedPoints = calculateProfilePoints(profilePoints.map((x) => [x.msec, x.dataValue]))
      const filteredInterpolatedPoints: number[][] = []
      const calcArray = [interpolatedPoints[0][0], ...interpolatedPoints.map((x) => x[3])]
      calcArray.forEach((x, index) => {
        const prev = index > 0 ? calcArray[index - 1] : null
        const next = index < calcArray.length ? calcArray[index + 1] : null
        if (!prev || !next || Math.max(prev[1], next[1], x[1]) !== Math.min(prev[1], next[1], x[1])) {
          filteredInterpolatedPoints.push(x)
        }
      })
      return filteredInterpolatedPoints
    }
    const isTempSeries = profileType !== ProfileType.POWER
    const profilePoints = datapointsToSeries(datapoints || [], getMainProfileParam(profileType))
    const mainSeriesPoints = calculateProfilePointsFromSeries(profilePoints)
    const fanPoints = calculateProfilePointsFromSeries(datapointsToSeries(datapoints || [], BasicDataParam.FAN))
    const rpmPoints = calculateProfilePointsFromSeries(datapointsToSeries(datapoints || [], BasicDataParam.RPM))

    const profileData: Profile = {
      ...getDefaultProfileData(profileType),
      ...(isTempSeries && {
        temperature_bezier: mainSeriesPoints,
      }),
      ...(profileType === ProfileType.POWER && {
        power_bezier: mainSeriesPoints,
      }),
      fan_bezier: fanPoints,
      rpm_bezier: rpmPoints,
      profile_type: profileType,
      name: t('logview.createprofilename').replace('{0}', getLogTitle()),
    }
    // send profile data as state, will be read in CreateProfileView
    navigate(`/machine/${state.currentMachine?.slug}/profiles/create`, {
      state: {
        profileData: profileData,
        logSeries: {
          data: profilePoints.map((x) => {
            return {
              ...x,
              dataValue: isTempSeries && x.dataValue !== null ? convertToTempScale(x.dataValue, 2) : x.dataValue,
            }
          }),
          param: getMainProfileParam(profileType),
          seriesType: isTempSeries ? SeriesType.TEMPERATURE : SeriesType.PERCENT,
        },
      },
    })
  }

  // load cupping scores for this log
  React.useEffect(() => {
    if (state.currentCustomer?.id) {
      get<PagedResponse<CuppingSession>>(`cuppingsessions/?customer=${state.currentCustomer.id}&log=${params.logId}`)
        .then((response) => setLogCuppingSessions(response.data.results))
        .catch(() => setHasLogCuppingSessionsError(true))
    }
  }, [state.currentCustomer?.id, params.logId])

  // update page title
  React.useEffect(() => {
    if (state.currentMachine?.name && logData) {
      setPageTitle(getLogName(logData, state.currentMachine?.name, t('logview.batch')))
    }
  }, [state.currentMachine?.name, logData, t])

  React.useEffect(() => {
    // load single log
    if (state.currentMachine?.url) {
      setHasLoadLogError(false)
      setIsLoadingLog(true)
      Promise.all([
        get<Log>(`logs/${params.logId}/`),
        get<Array<LegacyDatapoint>>(`datapoints/?page_size=all&log=${params.logId}`),
      ])
        .then((results) => {
          const [logResult, datapointsResult] = results
          setIsLoadingLog(false)
          if (state.currentMachine?.url !== logResult.data.machine) {
            setHasLoadLogError(true)
          } else {
            setLogData(logResult.data)
            setDatapoints(legacyDatapointsToDatapoints(datapointsResult.data))
            setEvents(getEventsFromLogOrDatapoints(logResult.data, datapointsResult.data))
          }
        })
        .catch(() => {
          setIsLoadingLog(false)
          setHasLoadLogError(true)
        })
    }
  }, [state.currentMachine?.url, params.logId])

  const getLogTitle = (): string => {
    return getLogName(logData, state.currentMachine?.name, t('logview.batch'))
  }

  const deleteLog = (log: Log): void => {
    setIsDeletingLog(true)
    setHasDeleteError(false)
    setIsDeleteLogModalOpen(false)

    remove(log.url)
      .then(() => {
        setIsDeletingLog(false)
        setHasDeleteLogSuccess(true)
      })
      .catch(() => {
        setIsDeletingLog(false)
        setHasDeleteError(true)
      })
  }

  const getExportFileName = (): string =>
    t('logview.csvfilename')
      .replace('{0}', state.currentMachine?.name || '')
      .replace('{1}', `${logData?.batch_no}`)
      .concat(`${logData?.name ? ' - ' + logData.name : ''}`)

  const logToCsv = (): void => {
    const NEWLINE = '\n'
    const SEPARATOR = ','

    const seriesConfig = getPlotSeriesFromLog(datapoints || [], getSensorconfig(), theme, state.user)
    const graphEventDatapoints = getLogEvents(
      events || [],
      null,
      '',
      datapointsToSeries(datapoints || [], BasicDataParam.BT)
    )
    const lastDatapoint = Math.max(...(datapoints || []).map((x) => x.msec))
    // create headers
    let csvContent =
      ['Time', 'Event', ...seriesConfig.map((x) => `${t(x.seriesLabel)} (${getValueSuffix(x.seriesType)})`)].join(
        SEPARATOR
      ) + NEWLINE
    for (let i = 0; i <= lastDatapoint; i += 1000) {
      // find event in current msec (if any)
      const currentGraphEvent =
        i === 0 ? { type: EventType.Charge } : graphEventDatapoints.events.find((x) => x.msec === i)
      const currentEventString =
        currentGraphEvent && currentGraphEvent.type !== null ? t(EventTypeString[currentGraphEvent.type]) : 'N/A'
      csvContent +=
        [
          i,
          currentEventString,
          ...seriesConfig.map((x) => {
            const isTempSeries = x.seriesType === SeriesType.TEMPERATURE || x.seriesType === SeriesType.ROR
            const msecDataValue = x.datapoints.find((y) => y.msec === i)?.dataValue
            return isTempSeries ? convertToTempScale(msecDataValue || 0, 2, x.param) : msecDataValue
          }),
        ].join(SEPARATOR) + NEWLINE
    }

    const a = document.createElement('a')
    a.download = `${getExportFileName()}.csv`
    a.href = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csvContent)
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const getProfileDataElement = (): JSX.Element => {
    if (isLoadingProfileData) {
      return <CircularProgress />
    } else if (isProfileDataAvailable) {
      return (
        <TooltipIconButton
          onClick={toggleProfileData}
          tooltip={t('logview.toggleprofileseries')}
          icon={<SsidChartOutlinedIcon />}
          isToggled={!!profileData}
        />
      )
    } else {
      return (
        <Tooltip title={t('logview.profileseriesunavailable')}>
          <SsidChartOutlinedIcon sx={{ mt: 0.5, mb: 0.5 }} color="disabled" />
        </Tooltip>
      )
    }
  }

  const profileDataLoaded = (profile: Profile | null): void => {
    if (profile && !isBtBasedProfile(profile.profile_type)) {
      setProfileData(profile)
    } else {
      setIsProfileDataAvailable(false)
    }
  }

  const toggleProfileData = (): void => {
    if (profileData) {
      setProfileData(null)
    } else if (logData?.profile_data) {
      profileDataLoaded(logData.profile_data)
    } else if (state.currentCustomer?.id && logData?.profile_name) {
      setIsLoadingProfileData(true)
      loadLogProfile(
        logData.profile_name,
        state.currentCustomer.id,
        (profile: Profile | null) => {
          setIsLoadingProfileData(false)
          profileDataLoaded(profile)
        },
        () => {
          setIsLoadingProfileData(false)
          setIsProfileDataAvailable(false)
        }
      )
    }
  }

  const navigateToLog = (relativeRequestUrl: string): void => {
    // reset states
    setIsLoadingLog(true)
    setLogData(null)
    setDatapoints(null)
    setEvents(null)
    setProfileData(null)
    setIsProfileDataAvailable(true)
    setLogCuppingSessions([])
    setHasLogCuppingSessionsError(false)
    setHasLoadLogError(false)

    get<Log>(relativeRequestUrl)
      .then((response) => {
        navigate(`/machine/${state.currentMachine?.slug}/logs/${response.data.id}`, {
          state: { compareLogData: [logData] },
        })
      })
      .catch(() => {
        setIsLoadingLog(false)
        setHasLoadLogError(true)
      })
  }

  return (
    <Container
      component="main"
      maxWidth={false}
      sx={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'column' }}
    >
      <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
        {isLoadingLog && <CircularProgress />}
        {hasLoadLogError && (
          <Stack flex={1}>
            <Alert severity="error">{t('logview.loadlogerror')}</Alert>
          </Stack>
        )}
        {isCompareLogModalOpen && logData && (
          <CompareLogDialog log={logData} onClose={() => setIsCompareLogModalOpen(false)} />
        )}
        {isCreateProfileModalOpen && (
          <CreateProfileFromLogModal
            onClose={() => setIsCreateProfileModalOpen(false)}
            createProfileFromLog={createProfileFromLog}
            canCreateInletTempProfile={logData?.sensor_config?.has_inlet}
          />
        )}
        {isInventoryDialogOpen && logData && state.currentCustomer && (
          <InventorySelector
            customer={state.currentCustomer}
            log={logData}
            onClose={() => setIsInventoryDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsInventoryDialogOpen(false)
            }}
          />
        )}
        {isBatchWeightDialogOpen && logData && state.currentMachine && (
          <BatchWeightDialog
            log={logData}
            maxBatchWeightInGrams={state.currentMachine.is_p2000 ? getP2000MaxWeight() : getL100MaxWeight()}
            onClose={() => setIsBatchWeightDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsBatchWeightDialogOpen(false)
            }}
          />
        )}
        {isBatchColorDialogOpen && logData && state.currentMachine && (
          <BatchColorDialog
            log={logData}
            onClose={() => setIsBatchColorDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsBatchColorDialogOpen(false)
            }}
          />
        )}
        {isEventsDialogOpen && logData && state.currentMachine && (
          <EditEventsDialog
            log={logData}
            maxTime={Math.max(...(datapoints || []).map((x) => x.msec))}
            onClose={() => setIsEventsDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsEventsDialogOpen(false)
              setEvents(getEventsFromLogOrDatapoints(newLogData, []))
            }}
          />
        )}
        {isNameDialogOpen && logData && (
          <LogChartTitle
            title={getLogTitle()}
            logUrl={logData.url}
            onClose={() => setIsNameDialogOpen(false)}
            onTitleChanged={(updatedLogData: Log) => {
              setLogData(updatedLogData)
              setIsNameDialogOpen(false)
            }}
          />
        )}
        <Backdrop open={isDeletingLog} sx={{ zIndex: 10 }}>
          <CircularProgress />
        </Backdrop>
        {isDeleteLogModalOpen && logData && (
          <ConfirmationDialog
            header={t('logview.deletelogheader').replace('{0}', getLogTitle())}
            body={t('logview.deletelogbody')}
            confirmButtonText={t('logview.deletelog')}
            type="error"
            onConfirm={() => deleteLog(logData)}
            onClose={() => setIsDeleteLogModalOpen(false)}
          />
        )}
        {hasDeleteLogSuccess && (
          <Stack spacing={2} alignItems="flex-start">
            <Alert severity="success">{t('logview.deletelogsuccess').replace('{0}', getLogTitle())}</Alert>
            <Button component={Link} sx={{ textAlign: 'center' }} to={`/machine/${state.currentMachine?.slug}/logs/`}>
              {t('logview.backtologs')}
            </Button>
          </Stack>
        )}
        {hasDeleteError && (
          <Stack>
            <Alert severity="error">{t('logview.deletelogerror')}</Alert>
          </Stack>
        )}
        {/* step 1 */}
        {shouldShowStep(ViewStep.Left) && logData && !hasDeleteLogSuccess && (
          <Stack width={isScreenBelowMdBreakpoint ? '100%' : LEFT_COLUMN_WIDTH}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigateToLog(`${logData.url}prev/`)}>
                {t('logview.prev')}
              </Button>
              <Button endIcon={<ArrowForwardIcon />} size="small" onClick={() => navigateToLog(`${logData.url}next/`)}>
                {t('logview.next')}
              </Button>
            </Stack>
            <LogInfoTable
              logs={[logData]}
              logDatapoints={datapoints ? { [logData.url]: datapoints } : {}}
              machine={state.currentMachine}
              onSetInventory={() => setIsInventoryDialogOpen(true)}
              onSetBatchWeight={() => setIsBatchWeightDialogOpen(true)}
              onSetColor={() => setIsBatchColorDialogOpen(true)}
              onSetName={() => setIsNameDialogOpen(true)}
            >
              <>
                <InfoElement
                  label={<Stack sx={{ py: 1 }}>{t('logview.cuppingsessions')}</Stack>}
                  value={
                    <Stack spacing={1}>
                      {logCuppingSessions && logCuppingSessions.length === 0 && (
                        <div>{t('logview.nocuppingsessions')}</div>
                      )}
                      {logCuppingSessions?.map((session) => {
                        const sample = session.cupping_samples.find((y) => y.log === logData.url)
                        return <CuppingLinkButton key={session.id} session={session} sample={sample} />
                      })}
                    </Stack>
                  }
                />
                {hasLogCuppingSessionsError && <Alert severity="error">{t('logview.loadcuppingsessionserror')}</Alert>}
              </>
            </LogInfoTable>
          </Stack>
        )}
        {/* step 2 */}
        {shouldShowStep(ViewStep.Center) && logData && datapoints && !hasDeleteLogSuccess && (
          <Stack
            ref={fullscreenRef}
            sx={{ height: '100%', flex: 1, overflow: 'hidden', mr: isScreenBelowMdBreakpoint ? 0 : 2 }}
          >
            <LogChart
              chartMaxTemp={getChartMaxTemp(state.user, state.currentMachine)}
              exportTitle={getExportFileName()}
              seriesConfig={getPlotSeriesFromLog(datapoints, getSensorconfig(), theme, state.user)}
              eventDatapoints={[
                getLogEvents(events || [], null, '', datapointsToSeries(datapoints, BasicDataParam.BT)),
              ]}
              compareLogSeries={[]}
              expectedSeries={getExpectedProfileSeries(theme, profileData)}
              rorInterval={getRorInterval(state.user)}
              downloadCsvButton={
                <TooltipIconButton onClick={logToCsv} tooltip={t('logview.downloadcsv')} icon={<DataObjectIcon />} />
              }
              showProfileDataButton={getProfileDataElement()}
              storeSeriesVisiblity={true}
              onRequestFullscreen={() => {
                if (fullscreenRef.current) {
                  fullscreenRef.current.requestFullscreen()
                }
              }}
            />
          </Stack>
        )}
        {/* step 3 */}
        {shouldShowStep(ViewStep.Right) && logData && !hasDeleteLogSuccess && (
          <Stack
            sx={{
              justifyContent: 'space-between',
              width: isScreenBelowMdBreakpoint ? '100%' : RIGHT_COLUMN_WIDTH,
              marginBottom: '20px',
            }}
            spacing={5}
          >
            <Stack sx={{ overflow: 'auto', pr: 0.5 }}>
              <Typography variant="h6">{t('logview.comments')}</Typography>
              <Stack>
                <Comments logId={logData.id} logUrl={logData.url} user={state.user} />
              </Stack>
            </Stack>
            <Stack spacing={1}>
              {logData.version > 1 && (
                <Button onClick={() => setIsEventsDialogOpen(true)}>{t('logview.moveevents')}</Button>
              )}
              <Button variant="outlined" color="error" onClick={() => setIsDeleteLogModalOpen(true)}>
                {t('logview.deletelog')}
              </Button>
              <Button variant="outlined" onClick={() => setIsCreateProfileModalOpen(true)}>
                {t('logview.createnewprofile')}
              </Button>
              <Button variant="outlined" onClick={() => setIsCompareLogModalOpen(true)}>
                {t('logview.comparelogs')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
      <Stack sx={{ mt: 1, mb: 4 }}>
        {isScreenBelowMdBreakpoint && (
          <ViewStepButtons
            steps={[ViewStep.Left, ViewStep.Center, ViewStep.Right]}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        )}
      </Stack>
    </Container>
  )
}

export default LogView
